import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { GridEventBus } from '../../core/event-bus/grid-event-bus';
import type { GridCellContext } from '../../core/interfaces/grid-context.types';
import { GridBridge } from '../../core/interfaces/grid-bridge.interface';
import type { CompanyColumnDef } from '../../core/interfaces/grid-column.types';
import type { GridConfig, GridSelectionMode } from '../../core/interfaces/grid-config.types';
import { DEFAULT_GRID_CONFIG } from '../../core/interfaces/grid-config.types';
import { resolveEllipsisTooltip } from '../../utils/grid-ellipsis-tooltip';

type SortFn<T> = (a: T, b: T) => number;

/**
 * Adapter concreto do Port `GridBridge` usando `nz-table` (Ant Design) como motor.
 *
 * Funcionalidades suportadas:
 * - Ordenação client-side por coluna (`sortFn` ou padrão lexicográfico/numérico)
 * - Filtro dropdown por coluna (`filterOptions` + `filterFn`)
 * - Linhas expansíveis com template customizável (`companyExpandDef`)
 * - Cabeçalho fixo (`scrollY`), scroll horizontal automático
 * - Colunas fixas à esquerda/direita (`pinned`)
 * - Exportação CSV nativa
 * - Todos os controles visuais do nz-table: borda, tamanho, título, rodapé, paginação
 */
@Component({
  selector: 'company-nztable-bridge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTableModule, NzTooltipModule, NgTemplateOutlet],
  templateUrl: './nz-table-bridge.component.html',
  styleUrl: './nz-table-bridge.styles.scss',
})
export class NzTableBridgeComponent<T = unknown> extends GridBridge<T> implements AfterViewInit, OnDestroy {
  private readonly eventBus = inject(GridEventBus, { optional: true });
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private resizeObserver: ResizeObserver | null = null;

  // ── Dados ─────────────────────────────────────────────────────────────────
  private readonly _hiddenFields = signal<ReadonlySet<string>>(new Set());
  private readonly _containerWidth = signal(0);
  private readonly _rowData = signal<readonly T[]>([]);

  readonly columns = signal<readonly CompanyColumnDef<T>[]>([]);
  readonly loading = signal(false);

  /** `nzData` requer array mutável; reexportamos como cópia rasa. */
  readonly tableData = computed(() => [...this._rowData()]);

  // ── Configuração visual ───────────────────────────────────────────────────
  readonly frontPagination = signal(DEFAULT_GRID_CONFIG.frontPagination);
  readonly showPagination = signal(DEFAULT_GRID_CONFIG.showPagination);
  readonly paginationType = signal<'default' | 'small'>(DEFAULT_GRID_CONFIG.paginationType);
  readonly paginationPosition = signal<'top' | 'bottom' | 'both'>(DEFAULT_GRID_CONFIG.paginationPosition);
  readonly simplePagination = signal(DEFAULT_GRID_CONFIG.simplePagination);
  readonly bordered = signal(DEFAULT_GRID_CONFIG.bordered);
  readonly tableSize = signal<'default' | 'middle' | 'small'>(DEFAULT_GRID_CONFIG.size);
  readonly title = signal<string | null>(DEFAULT_GRID_CONFIG.title);
  readonly footer = signal<string | null>(DEFAULT_GRID_CONFIG.footer);
  readonly expandable = signal(DEFAULT_GRID_CONFIG.expandable);
  readonly ellipsis = signal(DEFAULT_GRID_CONFIG.ellipsis);
  readonly scrollY = signal<string | null>(DEFAULT_GRID_CONFIG.scrollY);

  // ── Template de expansão ──────────────────────────────────────────────────
  readonly expandTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly _expandedIndices = signal<ReadonlySet<number>>(new Set());
  readonly expandedIndices = this._expandedIndices.asReadonly();

  // ── Ordenação (memoized por field) ────────────────────────────────────────
  private readonly _sortFnCache = new Map<string, SortFn<T>>();

  // ── Colunas visíveis ──────────────────────────────────────────────────────
  readonly visibleColumns = computed(() => {
    const hidden = this._hiddenFields();
    return this.columns().filter(col => !hidden.has(col.field));
  });

  /**
   * Larguras de coluna com suporte a `flex` proporcional via `ResizeObserver`.
   */
  readonly columnWidths = computed(() => {
    const cols = this.visibleColumns();
    const containerWidth = this._containerWidth();

    const fixedTotal = cols.reduce((sum, c) => sum + (c.width ?? 0), 0);
    const flexTotal = cols.reduce((sum, c) => sum + (c.flex ?? 0), 0);
    const remainingPx = Math.max(0, containerWidth - fixedTotal);

    const widths = new Map<string, string | null>();
    for (const col of cols) {
      if (col.width != null) {
        widths.set(col.field, `${col.width}px`);
      } else if (col.flex != null && flexTotal > 0 && remainingPx > 0) {
        widths.set(col.field, `${Math.floor((col.flex / flexTotal) * remainingPx)}px`);
      } else {
        widths.set(col.field, null);
      }
    }
    return widths;
  });

  /** Largura mínima total para scroll horizontal e colunas fixas. */
  readonly scrollX = computed(() => {
    const minWidth = this.visibleColumns().reduce((sum, col) => {
      if (col.width != null) return sum + col.width;
      if (col.minWidth != null) return sum + col.minWidth;
      return sum + 120;
    }, 0);
    return `${minWidth}px`;
  });

  readonly scrollConfig = computed<{ x?: string; y?: string }>(() => {
    const result: { x?: string; y?: string } = { x: this.scrollX() };
    const y = this.scrollY();
    if (y) result.y = y;
    return result;
  });

  /** tableLayout: "fixed" sempre que ellipsis está ativo (requisito do nz-table). */
  readonly tableLayout = computed<'auto' | 'fixed'>(() =>
    this.ellipsis() ? 'fixed' : 'auto'
  );

  /** Colspan para a linha de expansão — cobre todas as colunas visíveis + coluna de expand. */
  readonly expandColSpan = computed(() =>
    this.visibleColumns().length + (this.expandable() ? 1 : 0)
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      this._containerWidth.set(width);
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ── GridBridge: dados ─────────────────────────────────────────────────────

  override setColumns(columns: readonly CompanyColumnDef<T>[]): void {
    this.columns.set(columns);
    // Atualiza cache de funções de ordenação
    this._sortFnCache.clear();
    for (const col of columns) {
      if (col.sortFn) {
        this._sortFnCache.set(col.field, col.sortFn as SortFn<T>);
      } else if (col.sortable) {
        this._sortFnCache.set(col.field, this._makeDefaultSortFn(col.field));
      }
    }
  }

  override setRowData(rows: readonly T[]): void {
    this._rowData.set(rows);
  }

  override setLoading(loading: boolean): void {
    this.loading.set(loading);
  }

  // ── GridBridge: seleção ───────────────────────────────────────────────────

  override setSelectionMode(_mode: GridSelectionMode): void {
    // Seleção via companyCellDef (checkbox externo) — sem-op intencional.
  }

  override setSelectedRows(_rows: readonly T[]): void {
    // Seleção gerenciada externamente via companyCellDef.
  }

  // ── GridBridge: colunas ───────────────────────────────────────────────────

  override setColumnVisible(field: string, visible: boolean): void {
    this._hiddenFields.update(set => {
      const next = new Set(set);
      if (visible) next.delete(field);
      else next.add(field);
      return next;
    });
  }

  override setGlobalFilter(_term: string): void {
    // Filtragem global gerenciada pelo GridStateStore — o bridge recebe dados já filtrados.
  }

  // ── GridBridge: exportação ────────────────────────────────────────────────

  override exportToCsv(fileName: string): void {
    const cols = this.visibleColumns();
    const rows = this._rowData();
    const header = cols
      .map(c => `"${(c.headerName ?? c.field).replace(/"/g, '""')}"`)
      .join(',');
    const body = rows
      .map(row =>
        cols
          .map(col => {
            const val = (row as Record<string, unknown>)[col.field];
            const str = col.valueFormatter ? col.valueFormatter(val, row) : String(val ?? '');
            return `"${str.replace(/"/g, '""')}"`;
          })
          .join(','),
      )
      .join('\n');

    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  override sizeColumnsToFit(): void {
    // nz-table distribui espaço restante via CSS automaticamente.
  }

  // ── GridBridge: configuração visual (batch) ───────────────────────────────

  override configure(config: GridConfig): void {
    this.frontPagination.set(config.frontPagination);
    this.showPagination.set(config.showPagination);
    this.paginationType.set(config.paginationType);
    this.paginationPosition.set(config.paginationPosition);
    this.simplePagination.set(config.simplePagination);
    this.bordered.set(config.bordered);
    this.tableSize.set(config.size);
    this.title.set(config.title);
    this.footer.set(config.footer);
    this.expandable.set(config.expandable);
    this.ellipsis.set(config.ellipsis);
    this.scrollY.set(config.scrollY);
  }

  // ── GridBridge: expansão ──────────────────────────────────────────────────

  override setExpandTemplate(template: TemplateRef<unknown> | null): void {
    this.expandTemplate.set(template);
  }

  // ── Handlers de UI ───────────────────────────────────────────────────────

  onExpandChange(index: number, expand: boolean): void {
    this._expandedIndices.update(set => {
      const next = new Set(set);
      expand ? next.add(index) : next.delete(index);
      return next;
    });
  }

  onCellClick(row: T, rowIndex: number, col: CompanyColumnDef<T>, event: MouseEvent): void {
    this.eventBus?.publish({
      type: 'CELL_CLICKED',
      row,
      rowIndex,
      column: col as CompanyColumnDef<unknown>,
      modifiers: {
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        metaKey: event.metaKey,
      },
    });
  }

  // ── Helpers de template ───────────────────────────────────────────────────

  buildCellContext(
    row: T,
    rowIndex: number,
    col: CompanyColumnDef<T>,
  ): { $implicit: GridCellContext<T>; row: T; value: unknown; rowIndex: number; column: CompanyColumnDef<T> } {
    const value = (row as Record<string, unknown>)[col.field];
    const context: GridCellContext<T> = { value, row, rowIndex, column: col };
    return { $implicit: context, row, value, rowIndex, column: col };
  }

  getFieldValue(row: T, field: string): unknown {
    return (row as Record<string, unknown>)[field];
  }

  formatCellText(row: T, col: CompanyColumnDef<T>): string {
    const value = this.getFieldValue(row, col.field);
    if (col.valueFormatter) {
      return col.valueFormatter(value, row);
    }
    return value == null ? '' : String(value);
  }

  /** Tooltip exibido apenas quando o texto da célula está truncado por ellipsis. */
  private readonly _ellipsisTooltip = signal<string | null>(null);
  readonly ellipsisTooltip = this._ellipsisTooltip.asReadonly();

  onEllipsisTextEnter(event: MouseEvent, text: string): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      this._ellipsisTooltip.set(null);
      return;
    }
    this._ellipsisTooltip.set(resolveEllipsisTooltip(target, text));
  }

  /** Retorna a função de ordenação estável para a coluna (ou `false` se não ordenável). */
  getSortFn(col: CompanyColumnDef<T>): SortFn<T> | false {
    return this._sortFnCache.get(col.field) ?? false;
  }

  // ── Helpers privados ──────────────────────────────────────────────────────

  private _makeDefaultSortFn(field: string): SortFn<T> {
    return (a: T, b: T): number => {
      const va = (a as Record<string, unknown>)[field];
      const vb = (b as Record<string, unknown>)[field];
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
    };
  }
}
